var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * Inline "create a new vocabulary type" modal (CZNA-395).
 *
 * Escape hatch for the derivation-rule `vocabularyType` picker
 * (`DerivationRuleDetail`): the picker only offers OPEN types already present
 * in the `vocabulary_type` registry (CZNA-390 slice S1) — there was previously
 * no way to add a brand-new one from the GUI, only via the API directly. This
 * modal calls `POST /czna-authorities/vocabulary-types`
 * (`useCreateVocabularyType`) and, on success, hands the created type back to
 * the caller so it can be auto-selected and the registry refetched.
 *
 * `validationType` is fixed to `OPEN` (shown, but not editable) — a type
 * created here that ended up CLOSED would immediately vanish from the
 * OPEN-filtered picker that spawned this modal, silently breaking the
 * auto-select. Every other field from `CreateVocabularyTypeRequest` is
 * user-editable: `code` (validated client-side to fail fast, but the
 * authoritative check is the server's `VOCABULARY_` prefix guard),
 * `labelCs` (required), `labelEn`, `bibframeRole` (required — the server
 * 400s on blank), `readOnly`, `codeRequired`.
 */
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useForm, Controller } from 'react-hook-form';
import { Modal, ModalFooter, Button, TextField, Checkbox, Select, MessageBanner, } from '@folio/stripes/components';
import HelpTooltip from '../HelpTooltip';
import { useCreateVocabularyType } from '../../hooks/useVocabularyTypes';
import { getErrorInfo } from '../../utils/getErrorCode';
import { getErrorKind } from '../../utils/errorKind';
var CODE_PATTERN = /^VOCABULARY_[A-Z0-9_]{1,60}$/i;
var BIBFRAME_ROLES = ['RESOURCE', 'SCHEME_REF', 'INTERNAL'];
var EMPTY_FORM = {
    code: '',
    labelCs: '',
    labelEn: '',
    bibframeRole: '',
    readOnly: false,
    codeRequired: false,
};
var CreateVocabularyTypeModal = function (_a) {
    var open = _a.open, onClose = _a.onClose, onCreated = _a.onCreated;
    var intl = useIntl();
    var _b = useState(null), saveError = _b[0], setSaveError = _b[1];
    var _c = useForm({
        defaultValues: EMPTY_FORM,
        mode: 'onChange',
    }), control = _c.control, handleSubmit = _c.handleSubmit, reset = _c.reset, errors = _c.formState.errors;
    // Controlled-modal reset-on-open (ui-czna-development mandatory rule): a
    // second open must not resurface the previous attempt's input or error.
    useEffect(function () {
        if (open) {
            setSaveError(null);
            reset(EMPTY_FORM);
        }
    }, [open, reset]);
    var createMutation = useCreateVocabularyType({
        onSuccess: function (created) {
            setSaveError(null);
            onCreated(created);
        },
        onError: function (err) { return __awaiter(void 0, void 0, void 0, function () {
            var info;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, getErrorInfo(err)];
                    case 1:
                        info = _a.sent();
                        // CZNA-1625 — no answer in time: the type may have been created.
                        setSaveError(getErrorKind(info) === 'timeout'
                            ? intl.formatMessage({ id: 'ui-czna-authorities.error.timeout' })
                            : info.message || intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.createType.error' }));
                        return [2 /*return*/];
                }
            });
        }); },
    });
    var onSubmit = function (values) {
        setSaveError(null);
        createMutation.mutate({
            code: values.code.trim().toUpperCase(),
            labelCs: values.labelCs.trim(),
            labelEn: values.labelEn.trim() || undefined,
            validationType: 'OPEN',
            bibframeRole: values.bibframeRole,
            readOnly: values.readOnly,
            codeRequired: values.codeRequired,
        });
    };
    var bibframeRoleOptions = __spreadArray([
        { value: '', label: intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.createType.field.bibframeRole.placeholder' }) }
    ], BIBFRAME_ROLES.map(function (role) { return ({
        value: role,
        label: intl.formatMessage({ id: "ui-czna-authorities.derivationRules.createType.bibframeRole.".concat(role) }),
    }); }), true);
    var footer = (React.createElement(ModalFooter, null,
        React.createElement(Button, { buttonStyle: "primary", marginBottom0: true, type: "submit", form: "create-vocabulary-type-form", disabled: createMutation.isLoading },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.derivationRules.createType.action.save" })),
        React.createElement(Button, { buttonStyle: "default", marginBottom0: true, onClick: onClose, disabled: createMutation.isLoading },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.derivationRules.createType.action.cancel" }))));
    return (React.createElement(Modal, { open: open, onClose: onClose, label: intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.createType.title' }), dismissible: true, size: "medium", footer: footer },
        saveError && (React.createElement(MessageBanner, { type: "error", dismissible: true, onExited: function () { return setSaveError(null); } }, saveError)),
        React.createElement("form", { id: "create-vocabulary-type-form", onSubmit: handleSubmit(onSubmit) },
            React.createElement(Controller, { name: "code", control: control, rules: {
                    required: intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.createType.validation.code.required' }),
                    pattern: {
                        value: CODE_PATTERN,
                        message: intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.createType.validation.code.pattern' }),
                    },
                }, render: function (_a) {
                    var _b;
                    var field = _a.field;
                    return (React.createElement("div", null,
                        React.createElement(TextField, __assign({}, field, { label: intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.createType.field.code' }), placeholder: "VOCABULARY_MY_NEW_TYPE", error: (_b = errors.code) === null || _b === void 0 ? void 0 : _b.message, required: true, autoFocus: true })),
                        React.createElement(HelpTooltip, { testKey: "createType-code", messageId: "ui-czna-authorities.derivationRules.createType.help.code" })));
                } }),
            React.createElement(Controller, { name: "labelCs", control: control, rules: { required: intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.createType.validation.labelCs.required' }) }, render: function (_a) {
                    var _b;
                    var field = _a.field;
                    return (React.createElement(TextField, __assign({}, field, { label: intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.createType.field.labelCs' }), error: (_b = errors.labelCs) === null || _b === void 0 ? void 0 : _b.message, required: true })));
                } }),
            React.createElement(Controller, { name: "labelEn", control: control, render: function (_a) {
                    var field = _a.field;
                    return (React.createElement(TextField, __assign({}, field, { label: intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.createType.field.labelEn' }) })));
                } }),
            React.createElement(Controller, { name: "bibframeRole", control: control, rules: { required: intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.createType.validation.bibframeRole.required' }) }, render: function (_a) {
                    var _b;
                    var field = _a.field;
                    return (React.createElement("div", null,
                        React.createElement(Select, __assign({}, field, { label: intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.createType.field.bibframeRole' }), dataOptions: bibframeRoleOptions, error: (_b = errors.bibframeRole) === null || _b === void 0 ? void 0 : _b.message, required: true, onChange: function (e) { return field.onChange(e.target.value); } })),
                        React.createElement(HelpTooltip, { testKey: "createType-bibframeRole", messageId: "ui-czna-authorities.derivationRules.createType.help.bibframeRole" })));
                } }),
            React.createElement("div", null,
                React.createElement(Select, { label: intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.createType.field.validationType' }), value: "OPEN", dataOptions: [{ value: 'OPEN', label: intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.createType.validationType.OPEN' }) }], disabled: true, onChange: function () { } }),
                React.createElement(HelpTooltip, { testKey: "createType-validationType", messageId: "ui-czna-authorities.derivationRules.createType.help.validationType" })),
            React.createElement(Controller, { name: "readOnly", control: control, render: function (_a) {
                    var field = _a.field;
                    return (React.createElement(Checkbox, { name: field.name, label: intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.createType.field.readOnly' }), checked: field.value, onChange: function (e) { return field.onChange(e.target.checked); } }));
                } }),
            React.createElement(Controller, { name: "codeRequired", control: control, render: function (_a) {
                    var field = _a.field;
                    return (React.createElement(Checkbox, { name: field.name, label: intl.formatMessage({ id: 'ui-czna-authorities.derivationRules.createType.field.codeRequired' }), checked: field.value, onChange: function (e) { return field.onChange(e.target.checked); } }));
                } }))));
};
export default CreateVocabularyTypeModal;
